import React, { useState, useEffect } from 'react';
import './regionForm.css';
import { FaMapMarkerAlt, FaSave, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';
import { districtAPI } from '../../../utils/api';

/**
 * RegionForm Component
 * Form for creating and editing region records with validation
 *
 * @param {Object} props - Component props
 * @param {Object} props.region - Region data for editing (optional)
 * @param {Function} props.onSubmit - Callback function when form is submitted
 * @param {Function} props.onClose - Callback function to close the form
 * @param {boolean} props.isLoading - Loading state indicator
 */
const RegionForm = ({ region, onSubmit, onClose, isLoading = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    status: 'Active',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // District selection removed

  // Initialize form data when editing
  useEffect(() => {
    if (region) {
      setFormData({
        name: region.name || '',
        status: region.status || 'Active',
      });
    }
  }, [region]);

  // Validation functions
  const validateField = (name, value) => {
    if (name === 'name') {
      if (!value.trim()) return 'Region name is required';
      if (value.trim().length < 2) return 'Region name must be at least 2 characters';
      if (!/^[a-zA-Z\s]+$/.test(value.trim())) return 'Region name can only contain letters and spaces';
    }
    return '';
  };

  const validateForm = () => {
    const newErrors = {};
    newErrors.name = validateField('name', formData.name);
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fix the errors in the form',
        icon: 'error',
        confirmButtonColor: '#d33',
      });
      return;
    }
    setIsSubmitting(true);
    const payload = {
      name: formData.name,
      status: formData.status === 'Active' ? 'ACTIVE' : 'INACTIVE',
    };
    await onSubmit(payload);
    setFormData({ name: '', status: 'Active' });
    setErrors({});
    onClose();
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    if (Object.keys(formData).some(key => formData[key] !== '')) {
      Swal.fire({
        title: 'Discard Changes?',
        text: 'Are you sure you want to discard your changes?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, discard',
        cancelButtonText: 'Cancel',
      }).then(result => {
        if (result.isConfirmed) {
          onClose();
        }
      });
    } else {
      onClose();
    }
  };

  return (
    <div className="eduss-region-form">
      <form onSubmit={handleSubmit} className="eduss-region-form-content">
        <div className="eduss-region-form-group">
          <label htmlFor="name">
            <FaMapMarkerAlt /> Region Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter region name"
            className={errors.name ? 'error' : ''}
            disabled={isSubmitting || isLoading}
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>

        <div className="eduss-region-form-group">
          <label htmlFor="status">
            <FaMapMarkerAlt /> Status *
          </label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            disabled={isSubmitting || isLoading}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <div className="eduss-region-form-group">
          {/* Districts selection removed */}
        </div>

        <div className="eduss-region-form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="eduss-region-form-btn cancel"
            disabled={isSubmitting || isLoading}
          >
            <FaTimes /> Cancel
          </button>
          <button
            type="submit"
            className="eduss-region-form-btn submit"
            disabled={isSubmitting || isLoading}
          >
            <FaSave />{' '}
            {isSubmitting
              ? 'Saving...'
              : region
                ? 'Update Region'
                : 'Create Region'}
          </button>
        </div>
      </form>
    </div>
  );
};

RegionForm.propTypes = {
  region: PropTypes.object,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default RegionForm;
